import React, { ReactNode, useCallback } from 'react';

import { useSnackbar } from 'notistack';

import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import { IconButton, Link, List, ListItem, ListItemIcon, ListItemText, SxProps, Tooltip } from '@mui/material';

interface Item {
  value: string;
  icon?: ReactNode;
  tooltipText?: string;
  copyable?: boolean;
  copyableType?: 'text' | 'number';
  href?: string;
  title?: string;
}

const LIST_ITEM_ICON_STYLE: SxProps = {
  '&.MuiListItemIcon-root': {
    minWidth: '40px',
    flexShrink: 0,
  },
};

export default function IconList({ items }: { items: Array<Item> }) {
  const { enqueueSnackbar } = useSnackbar();

  const copyValue = useCallback(
    (value: string, title?: string, copyableType?: 'text' | 'number') => {
      if (copyableType === 'number') {
        navigator.clipboard.writeText(value.replace(/[^0-9]/g, ''));
      } else {
        navigator.clipboard.writeText(value);
      }

      enqueueSnackbar(`Copied ${title ? `${title} ` : ''}to clipboard`, { variant: 'success', autoHideDuration: 3500 });
    },
    [enqueueSnackbar],
  );

  return (
    <List>
      {items.map((item, index) => (
        <ListItem
          key={`${item.value}-${index}`}
          sx={{
            px: 0,
            py: 0.5,
            display: 'flex',
            alignItems: 'flex-center',
            width: '100%',
          }}
          secondaryAction={
            item.copyable && (
              <IconButton edge="end" onClick={() => copyValue(item.value, item.title, item.copyableType)}>
                <ContentCopyOutlinedIcon />
              </IconButton>
            )
          }
        >
          {item.tooltipText && (
            <Tooltip title={item.tooltipText}>
              <ListItemIcon sx={LIST_ITEM_ICON_STYLE}>{item.icon}</ListItemIcon>
            </Tooltip>
          )}
          {!item.tooltipText && <ListItemIcon sx={LIST_ITEM_ICON_STYLE}>{item.icon}</ListItemIcon>}
          <ListItemText
            primary={
              item.href ? (
                <Link href={item.href} target="_blank" rel="noopener noreferrer">
                  {item.value}
                </Link>
              ) : (
                item.value
              )
            }
            sx={{
              flex: '1 1 0%',
              minWidth: 0,
              marginRight: '45px',
              '& .MuiListItemText-primary': {
                wordBreak: 'break-word',
                whiteSpace: 'normal',
              },
            }}
          />
        </ListItem>
      ))}
    </List>
  );
}
